// pages/admin/Users.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, Space, Dropdown, Typography } from "antd";
import { TbDotsVertical } from "react-icons/tb";
import { PiKey, PiProhibit, PiSwap } from "react-icons/pi";
import { MdOutlineEdit, MdOutlineVpnKey } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import DataTable from "../../components/Tables/Datatable";
import SearchInput from "../../components/Forms/SearchInput";
import PrimaryButton from "../../components/Forms/PrimaryButton";
import CustomBadge from "../../components/Charts/CustomBadge";
import Loader from "../../common/Loader";

import Filters from "../../components/Filters";
import { useApiCall } from "../../services/api";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import ConfirmModal from "../../components/models/ConfirmModal";
import ChangeRole from "../../components/models/ChangeRole";
import { roleIds, roleNames } from "../../constant";
import usePagination from "../../hooks/usePagination";
import { HiKey, HiOutlineKey } from "react-icons/hi";
import { TiKeyOutline } from "react-icons/ti";
import ManagePermissions from "../../components/models/ManagePermissions";

import { PERMISSION_IDS } from "../../constant";
const actionKeyMap = {
  BLOCK_USER: "BLOCK_USER",
  DELETE_USER: "DELETE_USER",
  ALLOW_EDIT: "ALLOW_EDIT",
  GENERIC: "GENERIC_CONFIRM",
  CHANGE_ROLE: "CHANGE_ROLE",
};

const Users = () => {
  const { appliedFilters, ModalUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "filterRoleId",
        label: "Role",
        inputProps: { placeholder: "Select Role" },
        options: Object.entries(roleNames)
          .filter(([key, label]) => key != roleIds.Customer)
          .map(([value, label]) => ({
            value: Number(value),
            label,
          })),
      },
      {
        type: "select",
        key: "filterStatus",
        label: "Status",
        inputProps: { placeholder: "Select Status" },
        options: [
          { value: 0, label: "Active" },
          { value: 1, label: "Disabled" },
        ],
      },
    ],
    onChange: () => resetPage(),
  });

  const { postApi, loading } = useApiCall();
  const { showPopup } = useGlobalModal();
  const { listRefreshKey } = useSelector((state) => state.cartRefresh);
  const { user_id, full_name, is_admin, is_sales_manager, permission_ids = [] } = useSelector((state) => state.auth);
  const canViewAllSalespersons = is_admin || is_sales_manager || permission_ids.includes(PERMISSION_IDS.VIEW_ALL_SALESPERSONS);
  const canEditSalesperson = is_admin || is_sales_manager;

  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const { pagination, setPagination, handleChange: handlePaginationChange, resetPage } = usePagination();

  const [modalAction, setModalAction] = useState(null); // actionKey or null
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const fetchIdRef = useRef(0);

  const navigate = useNavigate();
  const { setTitle, setDescription, setBackButton } = useOutletContext();

  // Data fetching
  const fetchUsers = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setTableLoading(true);
      const { filterStatus, filterRoleId } = appliedFilters;
      const filters = { is_blocked: filterStatus, role_id: filterRoleId };
      const body = {
        per_page: pagination.pageSize,
        page: pagination.current,
        search: query,
        ...(Object.keys(appliedFilters).length ? filters : {}),
      };
      const res = await postApi("user/list", body);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      if (res && res.data) {
        setData(res.data);
        setTotalUsers(res.total);
      } else {
        setData([]);
        setTotalUsers(0);
      }
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("fetchUsers error:", err);
      setData([]);
      setTotalUsers(0);
    } finally {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setTableLoading(false);
    }
  }, [pagination, query, appliedFilters, listRefreshKey]);

  useEffect(() => {
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [fetchUsers]);

  // Table handlers
  const handleChange = (paginationInfo, filters, sorter) => {
    handlePaginationChange(paginationInfo, filters, sorter);
  };

  const handleRow = (record) => {
    if (record.user_id != user_id) {
      navigate("/admin/user-profile", {
        state: { user_id: record.user_id, routeType: "sales" },
      });
    }
  };

  // Dropdown action handler
  const handleMenuClick = (key, record) => {
    if (key === "EDIT") {
      handleRow(record);
      return;
    }

    if (key === "DELETE") {
      // prevent delete if assigned clients exist
      if (record?.customer_count > 0) {
        return showPopup({
          title: null,
          content: <span className="custom-modal-title">You cannot delete a sales employee with assigned clients. Please remove clients first.</span>,
          onOk: () => {},
          className: "custom-success-modal",
        });
      }
      setSelectedRecord(record);
      setModalAction(actionKeyMap.DELETE_USER);
      return;
    }

    if (key === "BLOCK") {
      setSelectedRecord(record);
      setModalAction(actionKeyMap.BLOCK_USER);
      return;
    }

    if (key === "CHANGE_ROLE") {
      if (record?.customer_count > 0) {
        return showPopup({
          title: null,
          content: (
            <span className="custom-modal-title">You cannot change role of a sales employee with assigned clients. Please remove clients first.</span>
          ),
          onOk: () => {},
          className: "custom-success-modal",
        });
      }
      setSelectedRecord(record);
      setModalAction(actionKeyMap.CHANGE_ROLE);
      return;
    }
  };

  // confirm modal handler
  const handleConfirmAction = async (selectedRole, data) => {
    if (!modalAction || !selectedRecord) return;

    const fixedRoleId =
      selectedRecord?.role_id === roleIds.SalesPerson
        ? roleIds.SalesManager
        : selectedRecord?.role_id === roleIds.SalesManager
          ? roleIds.SalesPerson
          : null;

    try {
      let res;
      if (modalAction === actionKeyMap.BLOCK_USER) {
        res = await postApi(
          "user/block-unblock-status",
          {
            is_blocked: selectedRecord?.is_blocked ? 0 : 1,
            updated_by: full_name,
            updated_by_id: user_id,
            user_id: selectedRecord?.user_id,
          },
          true,
        );
      } else if (modalAction === actionKeyMap.DELETE_USER) {
        res = await postApi(
          "user/delete",
          {
            user_id: selectedRecord?.user_id,
            updated_by_id: user_id,
            updated_by: full_name,
          },
          true,
        );
      } else if (modalAction === actionKeyMap.CHANGE_ROLE) {
        if (!fixedRoleId) {
          return showPopup({
            title: null,
            content: <span className="custom-modal-title">You can only swap roles between Salesperson and Sales Manager.</span>,
            onOk: () => {},
            className: "custom-success-modal",
          });
        }

        res = await postApi(
          "sales/change-user-role",
          {
            user_id: selectedRecord?.user_id,
            old_role_id: selectedRecord?.role_id,
            new_role_id: fixedRoleId,
          },
          true,
        );
      }

      if (res && res?.status) {
        setModalAction(null);
        setSelectedRecord(null);
        fetchUsers();
      }
    } catch (err) {
      console.error("action error:", err);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "user_id",
      key: "user_id",
      ellipsis: true,
      width: 100,
    },
    { title: "User", dataIndex: "full_name", key: "full_name", ellipsis: true },
    {
      title: "Role",
      dataIndex: "role_id",
      key: "role_id",
      ellipsis: true,
      render: (v) => roleNames[v],
    },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
    { title: "Phone", dataIndex: "phone", key: "phone", ellipsis: true },
    {
      title: "Status",
      dataIndex: "is_blocked",
      key: "is_blocked",
      ellipsis: true,
      render: (value) => {
        const statusNames = { 0: "Active", 1: "Disabled" };
        return <CustomBadge variant={value ? "danger" : "success"} text={statusNames[value]} />;
      },
    },
    {
      title: "",
      key: "action",
      fixed: "right",
      width: 60,
      render: (record) => (
        <>
          {record?.user_id !== user_id && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: "BLOCK",
                    label: (
                      <Space className="text-[#A33434] font-semibold">
                        <div className="bg-[#A33434] p-1.5 rounded-full text-[#ffffff]">
                          <PiProhibit />
                        </div>
                        {record.is_blocked ? "Unblock User" : "Block User"}
                      </Space>
                    ),
                    visible: record.user_id !== user_id,
                  },
                  {
                    key: "EDIT",
                    label: (
                      <Space className="text-[#A33434] font-semibold">
                        <div className="bg-[#A33434] p-1.5 rounded-full text-[#ffffff]">
                          <MdOutlineEdit />
                        </div>
                        Edit User
                      </Space>
                    ),
                    visible: record.user_id !== user_id,
                  },
                  {
                    key: "DELETE",
                    label: (
                      <Space className="text-[#A33434] font-semibold">
                        <div className="bg-[#A33434] p-1.5 rounded-full text-[#ffffff]">
                          <RiDeleteBin6Line />
                        </div>
                        Delete User
                      </Space>
                    ),
                    visible: record.user_id !== user_id,
                  },
                  {
                    key: "CHANGE_ROLE",
                    label: (
                      <Space className="text-[#A33434] font-semibold">
                        <div className="bg-[#A33434] p-1.5 rounded-full text-[#ffffff]">
                          <PiSwap />
                        </div>
                        Change Role
                      </Space>
                    ),
                    visible:
                      record.user_id !== user_id &&
                      !record?.is_blocked &&
                      [roleIds.SalesPerson, roleIds.SalesManager].includes(Number(record?.role_id)),
                  },
                ].filter((item) => item.visible !== false),
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleMenuClick(key, record);
                },
              }}
              trigger={["click"]}
              overlayClassName="custom-dropdown-sales-menu"
            >
              <Typography.Link
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Space>
                  <TbDotsVertical className="text-red-700" size={18} />
                </Space>
              </Typography.Link>
            </Dropdown>
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    setTitle("User Directory");
    setDescription(`${totalUsers} total users`);
    setBackButton(false);
  }, [setTitle, setDescription, setBackButton, totalUsers]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  return (
    <>
      <ModalUI />
      <div className="bg-[#FFFFFF]" style={{ borderRadius: "32px", overflow: "hidden" }}>
        <div className="flex flex-row justify-between items-center gap-3 w-full px-8 py-6">
          <SearchInput onSearch={() => {}} placeholder="Search for users" value={query} onChange={handleSearchInput} />

          <div className="flex items-center gap-3">
            <FilterButton />
            <PrimaryButton text="Customer Directory" onClick={() => navigate("/admin/customers")} noIcon />
            <Button
              type="primary"
              className="h-[45px] bg-[#7070701A]  hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-base !cursor-default"
              size="middle"
            >
              {totalUsers} users
            </Button>
          </div>
        </div>

        {tableLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader size={60} />
          </div>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data}
              handleChange={handleChange}
              handleRow={handleRow}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: totalUsers,
                showSizeChanger: true,
              }}
            />
          </>
        )}

        <div className="flex justify-end mt-4">
          <PrimaryButton
            text={"Manage Permissions"}
            icon={<MdOutlineVpnKey fill="#A33434" />}
            onClick={() => setIsPermissionsOpen(true)}
            iconOnleft
            btnType="ghost"
            iconClassName="!bg-[#a334341a] hover:!bg-[#a334341a]"
            className="me-5 mb-3"
          />
        </div>
      </div>

      <ConfirmModal
        actionKey={modalAction}
        record={selectedRecord}
        open={modalAction === actionKeyMap.BLOCK_USER || modalAction === actionKeyMap.DELETE_USER}
        onCancel={() => {
          setModalAction(null);
          setSelectedRecord(null);
        }}
        onConfirm={handleConfirmAction}
        loading={loading}
      />

      <ConfirmModal
        actionKey={actionKeyMap.CHANGE_ROLE}
        record={selectedRecord}
        newRoleLabel={
          selectedRecord?.role_id === roleIds.SalesPerson
            ? roleNames[roleIds.SalesManager]
            : selectedRecord?.role_id === roleIds.SalesManager
              ? roleNames[roleIds.SalesPerson]
              : ""
        }
        open={modalAction === actionKeyMap.CHANGE_ROLE}
        onCancel={() => {
          setModalAction(null);
          setSelectedRecord(null);
        }}
        onConfirm={handleConfirmAction}
        loading={loading}
        width={520}
      />

      <ManagePermissions isOpen={isPermissionsOpen} onClose={() => setIsPermissionsOpen(false)} />
    </>
  );
};

export default Users;
